import type { RouteOption } from "../../../api/threads/routeOptions";
import type { CapabilityOverridesContent } from "../../processAuthoring/capabilities/overrideDrafts";

/**
 * F39: where a model's context size is unknown, DMW asks for it where the model is picked and
 * saves the answer as a reviewed project setting, a `capability_overrides` item (capabilities.md
 * CAP-4, ADR-0017). Nothing is guessed: the question appears only while no source decided
 * `context_tokens`, and the answer is the number the person typed, never an estimate.
 */

export const CONTEXT_TOKENS = "context_tokens";

/** The model a question is about: the route's own identity, and the model ID or `*` for an agent's default. */
export interface ContextSizeSubject {
  readonly routeKind: RouteOption["route"]["kind"];
  readonly routeMatch: string;
  readonly modelMatch: string;
  /** An agent's own estimate, shown for reference and never saved on its own (D17). */
  readonly hint: number | null;
}

/** The subject to ask about, or `null` when the context size is known or the route can't be matched. */
export function unknownContextSizeOf(option: RouteOption | undefined): ContextSizeSubject | null {
  if (option === undefined || !option.available || option.routeIdentity === null) return null;
  const resolved = option.capabilities.find((capability) => capability.key === CONTEXT_TOKENS);
  if (resolved?.decided != null) return null;
  const hint = resolved?.hint?.value;
  return {
    routeKind: option.route.kind,
    routeMatch: option.routeIdentity,
    modelMatch: option.route.model_id ?? "*",
    hint: typeof hint === "number" ? hint : null,
  };
}

/** A positive whole number of tokens, or `null` for anything else: then nothing can be proposed. */
export function contextTokensOfText(text: string): number | null {
  if (!/^\d+$/u.test(text.trim())) return null;
  const tokens = Number.parseInt(text.trim(), 10);
  return Number.isSafeInteger(tokens) && tokens > 0 ? tokens : null;
}

/** The new overrides item with one entry: this model's context size, and where the person read it. */
export function contextSizeItem(subject: ContextSizeSubject, tokens: number, note: string, name: string): CapabilityOverridesContent {
  return {
    kind: "capability_overrides",
    name,
    description: "",
    entries: [{ route_kind: subject.routeKind, route_match: subject.routeMatch, model_match: subject.modelMatch, key: CONTEXT_TOKENS, value: tokens, note }],
  };
}
